import { createContext, useContext, useEffect, useState } from "react";

const CJK = /[\u3400-\u9fff\uff00-\uffef\u3000-\u303f]/;
// "Teks Indonesia / 中文" → pilih salah satu sisi. Pemisah: " / " dengan CJK di kanan.
const SEG = /([^/•|\n]*?)\s*\/\s*([^/•|\n]*[\u3400-\u9fff][^/•|\n]*)/g;
const orig = new WeakMap();
const last = new WeakMap();

function pick(text, lang) {
  if (lang === "both" || !CJK.test(text)) return text;
  return text.replace(SEG, (m, a, b) => {
    if (!CJK.test(b) || CJK.test(a)) return m;
    return lang === "id" ? a : b;
  });
}

function applyNode(node, lang) {
  if (node.nodeType === 3) {
    if (!orig.has(node)) orig.set(node, node.nodeValue);
    const o = orig.get(node);
    const v = pick(o, lang);
    last.set(node, v);
    if (node.nodeValue !== v) node.nodeValue = v;
    return;
  }
  if (node.nodeType !== 1) return;
  for (const attr of ["placeholder", "title"]) {
    if (node.hasAttribute && node.hasAttribute(attr)) {
      const key = `__i18n_${attr}`;
      if (node[key] === undefined) node[key] = node.getAttribute(attr);
      const v = pick(node[key], lang);
      if (node.getAttribute(attr) !== v) node.setAttribute(attr, v);
    }
  }
  if (node.tagName === "OPTION" && node.childNodes.length === 0 && node.label) return;
  node.childNodes.forEach((c) => applyNode(c, lang));
}

const Ctx = createContext({ lang: "both", setLang: () => {} });
export const useLang = () => useContext(Ctx);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("wms_lang") || "both");
  useEffect(() => {
    localStorage.setItem("wms_lang", lang);
    document.documentElement.lang = lang === "zh" ? "zh" : "id";
    const root = document.getElementById("root");
    if (!root) return;
    let busy = false;
    const run = () => { if (busy) return; busy = true; applyNode(root, lang); busy = false; };
    run();
    const obs = new MutationObserver((muts) => {
      if (busy) return;
      busy = true;
      muts.forEach((m) => {
        if (m.type === "characterData") { if (m.target.nodeValue !== last.get(m.target)) { orig.set(m.target, m.target.nodeValue); applyNode(m.target, lang); } }
        else { m.addedNodes.forEach((n) => applyNode(n, lang)); if (m.type === "attributes") { m.target[`__i18n_${m.attributeName}`] = m.target.getAttribute(m.attributeName); applyNode(m.target, lang); } }
      });
      busy = false;
    });
    obs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["placeholder", "title"] });
    return () => obs.disconnect();
  }, [lang]);
  return <Ctx.Provider value={{ lang, setLang }}>{children}</Ctx.Provider>;
}

export function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div className="lang-switch" data-testid="lang-switch">
      {[["id", "ID"], ["both", "ID·中"], ["zh", "中文"]].map(([k, l]) => <button key={k} className={lang === k ? "on" : ""} onClick={() => setLang(k)} data-testid={`lang-${k}`}>{l}</button>)}
    </div>
  );
}
